'use client';

import { useEffect, useState } from 'react';
import { CheckCircle, Clock, CalendarBlank } from '@phosphor-icons/react';
import { Input } from '@/components/seasons/Input';

interface Props {
  slug: string;
  webinarTitle: string;
  heroImg?: string;
  airsAt?: string;
  durationMinutes?: string | number;
  dek?: string;
  /** Pre-built Google Calendar URL to surface in the success state. */
  calendarUrl?: string;
  /** Override the submit button label. Defaults to 'Register now'. */
  ctaLabel?: string;
  /** Compact mode for use inside the in-player gate overlay. */
  compact?: boolean;
  /** When true the form skips its own h2 — the parent renders the heading. */
  hideHeading?: boolean;
  /** Callback fired after a successful registration. */
  onSubmitted?: () => void;
  /** Read server-side in page.tsx from the request headers — see getClientIp.
   * Falls back to a client-side fetch('/api/get-ip') when not provided (e.g.
   * the admin editor preview, which has no server-rendered page of its own). */
  visitorIp?: string;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  jobTitle: string;
}

/**
 * Parses both ISO strings ("2026-06-17T17:30:00.000Z") and free-form strings
 * ("June 17, 2026 | 12:30 PM EST | Virtual Event") into a Date object.
 * Returns null if the value cannot be reasonably parsed.
 */
function parseAirsAt(val: string): Date | null {
  if (!val) return null;

  // 1. Direct ISO / standard parse
  const direct = new Date(val);
  if (!isNaN(direct.getTime())) return direct;

  // 2. Free-form with pipe: "Month DD, YYYY | H:MM AM/PM TZ | ..."
  const parts = val.split('|').map((p) => p.trim());
  const datePart = parts[0];
  const timePart = parts[1];

  if (datePart && timePart) {
    // Extract start time — take content before any range separator (" - ", "–", " to ")
    const startTime = timePart.split(/\s+[-–]\s+|\s+to\s+/i)[0].trim();
    // Strip trailing timezone abbreviation (EST, EDT, ET, PT, PST, CDT, GST …)
    const timeNoTz = startTime.replace(/\s+[A-Z]{2,4}$/, '').trim();
    const combined = `${datePart} ${timeNoTz}`;
    const d = new Date(combined);
    if (!isNaN(d.getTime())) return d;
  }

  // 3. Free-form without pipe: "Month DD, YYYY H:MM AM/PM TZ"
  const noTz = val.replace(/\s+[A-Z]{2,4}$/, '').trim();
  const d3 = new Date(noTz);
  if (!isNaN(d3.getTime())) return d3;

  // 4. Date part only (loses time but better than nothing)
  if (datePart) {
    const d = new Date(datePart);
    if (!isNaN(d.getTime())) return d;
  }

  return null;
}

function getDurationMs(durationMinutes?: string | number): number {
  if (durationMinutes !== undefined && durationMinutes !== null && durationMinutes !== '') {
    const n = typeof durationMinutes === 'number'
      ? durationMinutes
      : parseFloat(String(durationMinutes));
    if (!isNaN(n) && n > 0) return Math.round(n) * 60 * 1000;
  }
  return 60 * 60 * 1000; // default 1 hour
}

function fmtUtc(d: Date): string {
  return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}

function escapeIcs(s: string): string {
  return s
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

function buildDescription(
  webinarTitle: string,
  slug: string,
  airsAt: string,
  dek?: string,
): string {
  const lines = [
    `You're registered for this Innovaccer webinar.`,
    '',
    `📅 ${airsAt}`,
  ];
  if (dek) lines.push('', dek);
  lines.push('', `Join at: https://innovaccer.com/resources/webinars/${slug}`);
  lines.push('', 'Please check your inbox for the confirmation email and join link.');
  return lines.join('\n');
}

function buildGoogleCalUrl(
  title: string,
  airsAt: string,
  durationMinutes?: string | number,
  dek?: string,
  slug?: string,
): string {
  const start = parseAirsAt(airsAt);
  const params = new URLSearchParams({ action: 'TEMPLATE', text: title });

  if (start) {
    const end = new Date(start.getTime() + getDurationMs(durationMinutes));
    params.set('dates', `${fmtUtc(start)}/${fmtUtc(end)}`);
  }

  params.set(
    'details',
    buildDescription(title, slug ?? '', airsAt, dek),
  );
  params.set('location', 'Virtual Event — join link sent via email');

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function buildIcsDataUrl(
  title: string,
  slug: string,
  airsAt: string,
  durationMinutes?: string | number,
  dek?: string,
): string {
  const start = parseAirsAt(airsAt);
  const description = buildDescription(title, slug, airsAt, dek);

  let dtstart: string;
  let dtend: string;

  if (start) {
    const end = new Date(start.getTime() + getDurationMs(durationMinutes));
    dtstart = fmtUtc(start);
    dtend = fmtUtc(end);
  } else {
    // Fallback: placeholder times one week from now
    const fallback = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    dtstart = fmtUtc(fallback);
    dtend = fmtUtc(new Date(fallback.getTime() + getDurationMs(durationMinutes)));
  }

  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Innovaccer Webinars//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${slug}@innovaccer.com`,
    `DTSTART:${dtstart}`,
    `DTEND:${dtend}`,
    `SUMMARY:${escapeIcs(title)}`,
    `DESCRIPTION:${escapeIcs(description)}`,
    `LOCATION:Virtual Event - https://innovaccer.com/resources/webinars/${escapeIcs(slug)}`,
    `URL:https://innovaccer.com/resources/webinars/${slug}`,
    'STATUS:CONFIRMED',
    'TRANSP:OPAQUE',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  return `data:text/calendar;charset=utf8,${encodeURIComponent(ics)}`;
}

/** Display the airsAt value in the success card. ET timezone for ISO; raw string for free-form. */
function formatSuccessDate(val?: string): string {
  if (!val) return '';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
  if (dateOnly) {
    const d = new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
  const d = new Date(val);
  if (!isNaN(d.getTime())) {
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'America/New_York',
    });
  }
  // Free-form: return the full string as the date label; no separate time needed
  return val;
}

function formatSuccessTime(val?: string): string {
  if (!val) return '';
  if (/^(\d{4})-(\d{2})-(\d{2})$/.test(val)) return ''; // date-only: no time to show
  const d = new Date(val);
  if (!isNaN(d.getTime())) {
    return (
      d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'America/New_York',
      }) + ' ET'
    );
  }
  // Free-form string already contains time — don't show a duplicate
  return '';
}

const HS_PORTAL_ID = '14536254';
const HS_WEBINAR_FORM_GUID = 'e1ce882c-e792-49bb-84f5-698a163d18eb';

async function submitToHubspot(form: FormState, slug: string, webinarTitle: string, ip: string) {
  const res = await fetch(
    `https://api.hsforms.com/submissions/v3/integration/submit/${HS_PORTAL_ID}/${HS_WEBINAR_FORM_GUID}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fields: [
          { name: 'firstname', value: form.firstName || '' },
          { name: 'lastname',  value: form.lastName  || '' },
          { name: 'email',     value: form.email     || '' },
          { name: 'company',   value: form.company   || '' },
          { name: 'jobtitle',   value: form.jobTitle  || '' },
          { name: 'collateral_title', value: webinarTitle },
          { name: 'collateral_type',  value: 'Webinar' },
          { name: 'collateral',       value: window.location.href },
          // Omitted when empty so a blank value is never sent as a form field.
          ...(ip ? [{ name: 'ipaddress', value: ip }] : []),
        ],
        // HubSpot rejects the whole submission with INVALID_IP_ADDRESS if
        // ipAddress is present but empty — omit the key instead of sending ''.
        context: { pageUri: window.location.href, pageName: webinarTitle, ...(ip ? { ipAddress: ip } : {}) },
      }),
    }
  );
  if (!res.ok) throw new Error('Registration failed. Please try again.');
}

export function WebinarRegistrationForm({
  slug,
  webinarTitle,
  heroImg,
  airsAt,
  durationMinutes,
  dek,
  calendarUrl,
  ctaLabel,
  compact = false,
  hideHeading = false,
  onSubmitted,
  visitorIp,
}: Props) {
  const [form, setForm] = useState<FormState>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hiddenFields, setHiddenFields] = useState({ pageName: '', pageURI: '', pageID: '', ipAddress: '', hutk: '' });

  useEffect(() => {
    const hutk = document.cookie.split('; ').find(r => r.startsWith('hubspotutk='))?.split('=')[1] ?? '';
    // Prefer the server-read IP passed down as a prop. Only fall back to the
    // client-side round trip when no caller provided one (e.g. the admin
    // editor preview, which renders this form with no page.tsx of its own).
    if (visitorIp !== undefined) {
      setHiddenFields({
        pageName: document.title,
        pageURI: window.location.href,
        pageID: window.location.pathname,
        ipAddress: visitorIp,
        hutk,
      });
      return;
    }
    fetch('/api/get-ip').then(r => r.json()).catch(() => ({ ip: '' })).then(({ ip }) => {
      setHiddenFields({
        pageName: document.title,
        pageURI: window.location.href,
        pageID: window.location.pathname,
        ipAddress: ip ?? '',
        hutk,
      });
    });
  }, [visitorIp]);

  function update<K extends keyof FormState>(key: K) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    // Gated — onSubmitted (which unlocks the on-demand video) only fires once
    // HubSpot actually accepts the submission. Previously the gated-video
    // path unlocked immediately regardless of the result, so a rejected
    // submission (e.g. the INVALID_IP_ADDRESS bug) silently lost the lead
    // while the visitor got the video anyway with no indication of failure.
    try {
      await submitToHubspot(form, slug, webinarTitle, hiddenFields.ipAddress);
      setSubmitted(true);
      onSubmitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    const dateLabel = formatSuccessDate(airsAt);
    const timeLabel = formatSuccessTime(airsAt);

    return (
      <div className={`wbn-form wbn-form--success${compact ? ' wbn-form--compact' : ''}`} id="wbn-register">
        <CheckCircle size={40} weight="fill" style={{ color: 'var(--seasons-color-cyber-blue)' }} />
        <h2 className="type-heading-lg" style={{ marginTop: 'var(--seasons-space-3)' }}>
          You&apos;re registered
        </h2>
        <p className="type-body-md" style={{ color: 'var(--seasons-color-text-secondary)', marginTop: 'var(--seasons-space-2)' }}>
          We&apos;ll email a calendar invite and a reminder before the session.
        </p>
        {airsAt && (
          <>
            <div className="wbn-form__success-meta">
              {dateLabel && (
                <span>
                  <CalendarBlank size={14} weight="regular" /> {dateLabel}
                </span>
              )}
              {timeLabel && (
                <span>
                  <Clock size={14} weight="regular" /> {timeLabel}
                </span>
              )}
            </div>
            {/* Temporarily hidden: Add to Google Calendar + Download .ics. Remove the display:none style to restore. */}
            <div className="wbn-form__cal-actions" style={{ display: 'none' }}>
              <a
                href={buildGoogleCalUrl(webinarTitle, airsAt, durationMinutes, dek, slug)}
                target="_blank"
                rel="noopener noreferrer"
                className="block-button-link"
                style={{ width: '100%' }}
              >
                <span className="block-button block-button--md block-button--midnight">
                  <span className="block-button__text">
                    <span className="btn-primary-text">Add to Google Calendar</span>
                    <span className="btn-hover-text" aria-hidden="true">Add to Google Calendar</span>
                  </span>
                </span>
              </a>
              <a
                href={buildIcsDataUrl(webinarTitle, slug, airsAt, durationMinutes, dek)}
                download={`${slug}.ics`}
                className="wbn-form__ics-link"
              >
                Download .ics (Outlook / Apple Calendar)
              </a>
            </div>
          </>
        )}
        {calendarUrl && (
          <a
            href={calendarUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block-button-link"
            style={{ marginTop: 'var(--seasons-space-6)', display: 'inline-flex' }}
          >
            <span className="block-button block-button--md block-button--midnight">
              <span className="block-button__text">
                <span className="btn-primary-text">Add to calendar</span>
                <span className="btn-hover-text" aria-hidden="true">Add to calendar</span>
              </span>
            </span>
          </a>
        )}
      </div>
    );
  }

  return (
    <form
      id="wbn-register"
      className={`wbn-form${compact ? ' wbn-form--compact' : ''}`}
      onSubmit={handleSubmit}
      aria-label={`Register for ${webinarTitle}`}
    >
      <input type="hidden" name="collateral_title" value={hiddenFields.pageName} />
      <input type="hidden" name="collateral"       value={hiddenFields.pageURI} />
      <input type="hidden" name="collateral_type"  value={hiddenFields.pageID} />
      <input type="hidden" name="ipAddress"        value={hiddenFields.ipAddress} />
      <input type="hidden" name="hutk"             value={hiddenFields.hutk} />
      {!compact && heroImg && (
        <div className="wbn-form__cover">
          <img src={heroImg} alt="" />
        </div>
      )}
      <div className="wbn-form__body">
        {!hideHeading && (
          <h2 className="type-heading-lg wbn-form__heading">
            {compact ? 'Keep watching — register to continue' : 'Save your seat'}
          </h2>
        )}
        <div className="wbn-form__row">
          <Input id="wbn-first" label="First name" required value={form.firstName} onChange={update('firstName')} />
          <Input id="wbn-last" label="Last name" required value={form.lastName} onChange={update('lastName')} />
        </div>
        <Input id="wbn-email" label="Work email" type="email" required value={form.email} onChange={update('email')} />
        <Input id="wbn-company" label="Company" required value={form.company} onChange={update('company')} />
        <Input id="wbn-title" label="Job title" value={form.jobTitle} onChange={update('jobTitle')} />
        {error && (
          <p className="type-body-sm" style={{ color: 'var(--seasons-color-text-danger)' }}>
            {error}
          </p>
        )}
        <button type="submit" className="block-button-link" disabled={submitting} style={{ width: '100%' }}>
          <span className={`block-button block-button--md block-button--midnight${submitting ? ' is-loading' : ''}`}>
            <span className="block-button__text">
              <span className="btn-primary-text">
                {submitting ? 'Submitting…' : (ctaLabel ?? (compact ? 'Continue watching' : 'Register now'))}
              </span>
              <span className="btn-hover-text" aria-hidden="true">
                {submitting ? 'Submitting…' : (ctaLabel ?? (compact ? 'Continue watching' : 'Register now'))}
              </span>
            </span>
          </span>
        </button>
        <p className="type-body-sm wbn-form__legal">
          By submitting, you agree to the processing of your personal data as described in the Privacy Statement.
        </p>
      </div>
    </form>
  );
}
